import { ReactChildren } from 'types/global';

import Breadcrumbs from 'molecules/Breadcrumbs';
import Heading from 'molecules/Heading';
import Section from 'molecules/Section';
import Video from 'molecules/Video';

import { hasArrayValues } from 'utils/array/arrays';

import type { FC } from 'react';

interface HeroResourcePropsWithChildren extends Omit<HeroResourceProps, 'tags'>, ReactChildren {
  nextComponentIsLight?: boolean;
  tags?: string[] | { label: string }[];
  hasTags: boolean;
  hideBreadcrumbs?: boolean;
}

const HeroResource: FC<HeroResourcePropsWithChildren> = ({
  tags,
  title,
  summary,
  featuredVideo,
  featuredVideoThumbnail,
  nextComponentIsLight,
  children,
  breadcrumbTitle,
  hasTags = true,
  hideBreadcrumbs = false,
}) => (
  <Section
    darkMode
    nextComponentIsDark={!nextComponentIsLight || false}
    hasGradientTransition
    paddingTop="md"
    paddingBottom="xl"
  >
    <div className="flex flex-col gap-8 sm:gap-12 lg:gap-14">
      {!hideBreadcrumbs && <Breadcrumbs breadcrumbTitle={breadcrumbTitle} />}
      <div className="mx-auto flex max-w-[800px] flex-col items-center justify-center gap-4 sm:gap-6">
        {hasTags && hasArrayValues(tags) && (
          <div className="flex flex-wrap gap-4">
            {tags.map(tag => {
              const label = typeof tag === 'string' ? tag : tag.label;

              return (
                <span
                  className="font-spaceGrotesk font-semibold text-eyebrow text-overline-sm dark:text-primary-300"
                  key={label}
                >
                  {label}
                </span>
              );
            })}
          </div>
        )}
        <Heading heading={title} body={summary} alignment="center" size="lg" as="h1" animateOnMount>
          {children && children}
        </Heading>
      </div>
      {featuredVideo && (
        <div className="relative w-full">
          <Video
            videoSrc={featuredVideo}
            thumbnail={featuredVideoThumbnail}
            className="size-full max-w-[1016px] object-cover"
            priority
          />
        </div>
      )}
    </div>
  </Section>
);

export default HeroResource;
